import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Users, Award, Target, Heart, ArrowRight, CheckCircle2 } from 'lucide-react';
import { companyInfo, stats } from '../data/mock';

const UeberUns = () => {
  const values = [
    { icon: Target, title: 'Zielorientiert', desc: 'Wir arbeiten ergebnisorientiert und liefern messbare Resultate.' },
    { icon: Heart, title: 'Leidenschaft', desc: 'Wir lieben was wir tun und das sieht man in unserer Arbeit.' },
    { icon: Award, title: 'Qualität', desc: 'Höchste Standards in Design und Entwicklung.' },
    { icon: Users, title: 'Partnerschaft', desc: 'Langfristige Zusammenarbeit auf Augenhöhe.' },
  ];

  const team = [
    { name: companyInfo.owner, role: 'Gründer & Geschäftsführer', desc: 'Experte für Webentwicklung und digitale Strategien.' },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-6">
              <Users size={16} />
              Über uns
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ihre Partner für digitalen Erfolg
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {companyInfo.name} - Wir sind eine Full-Service-Webagentur aus Düsseldorf und entwickeln digitale Lösungen, die begeistern.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-blue-600">{stat.value}</div>
                <div className="text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Unsere Geschichte</h2>
          </div>
          <div className="prose prose-lg max-w-none text-slate-600">
            <p>
              {companyInfo.name} wurde mit einer klaren Vision gegründet: Unternehmen dabei zu helfen, 
              ihre digitale Präsenz auf das nächste Level zu bringen. Was als Ein-Mann-Unternehmen begann, 
              ist heute eine etablierte Webagentur, die Kunden aus verschiedensten Branchen betreut.
            </p>
            <p>
              Unser Fokus liegt auf der Entwicklung von maßgeschneiderten Webseiten und Webanwendungen, 
              die nicht nur gut aussehen, sondern auch messbare Ergebnisse liefern. Wir glauben daran, 
              dass gutes Design und solide Technik Hand in Hand gehen müssen.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">Unsere Werte</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Card key={i} className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <v.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-600">{v.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">Das Team</h2>
          </div>
          <div className="max-w-md mx-auto">
            {team.map((member, i) => (
              <Card key={i} className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                    <Users className="text-blue-600" size={40} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{member.name}</h3>
                  <p className="text-blue-600 mb-4">{member.role}</p>
                  <p className="text-slate-600">{member.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Kontakt aufnehmen</h2>
          <p className="text-slate-400 mb-4">
            {companyInfo.address}<br />
            Tel: {companyInfo.phone}<br />
            E-Mail: {companyInfo.email}
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white mt-4">
              Projekt besprechen
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default UeberUns;
